import * as React from "react";
import { IActivePanelState } from "source/app/presentation/Shared/ActivePanel/IActivePanelState";
import { AddStatusMessage } from "../../../presentation/MainPanel/StatusBar/StatusBar";
import { EStatusMessageType } from "../../../presentation/MainPanel/StatusBar/StatusMessageType/StatusMessageType";
import { TRenderable } from "../../../presentation/Shared/SharedTypes";
import { Style } from "../../../presentation/Shared/Styles/CSS";
import { ESharedComponent } from "../State/ESharedComponent";
import { State } from "../State/State";
import { IKeyboardEvent, TKeyboardEvent } from "./IKeyboardEvents";

export const WithKeyboardHotkeys = ({
    name,
    keyboardEvents,
    children,
}: {
    name: string;
    keyboardEvents: IKeyboardEvent[];
    children: TRenderable;
}) => {
    // localized keyboard hotkeys require the element to be in focus
    // hence we are using a ref to focus on mount
    const divElement = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => divElement.current!.focus(), []);

    return (
        <div
            id={`hotkeyTarget_${name}`}
            onKeyDown={(event) => HandleKeyboardEvent(keyboardEvents, event)}
            tabIndex={0}
            ref={divElement}
            {...Style(`keyboardHotkeyWrapper`, { outlineStyle: `none` })}
        >
            {children}
        </div>
    );
};

export function HandleKeyboardEvent(
    keyboardEvents: IKeyboardEvent[],
    event: TKeyboardEvent
) {
    keyboardEvents.forEach(async (ke: IKeyboardEvent) => {
        if (ke.trigger(event)) {
            const conditionToExecute = GetConditionToExecute(ke);

            await AddStatusMessage(
                `${ke.hotkeyDescription} - ${ke.actionDescription}`,
                EStatusMessageType.info
            );

            conditionToExecute();
        }
    });
}

function GetConditionToExecute(ke: IKeyboardEvent) {
    if (ke.action) {
        return ke.action;
    }

    if (!ke.conditions) {
        return () => {};
    }

    const activePanel = State.ReadClass<IActivePanelState>(
        ESharedComponent.ActivePanel
    );

    const { activePanelId } = activePanel.state;

    if (!activePanel || !activePanelId) {
        return () => {};
    }

    const matchingConditions = ke.conditions!.filter(
        (condition) => condition.when === activePanelId!
    );

    if (matchingConditions.length !== 1) {
        return () => {};
    }

    return matchingConditions[0].do;
}
